import React, {useRef} from 'react';
import {observer} from 'mobx-react';
import {buttonMultilineShipmentsStore} from '../button-multiline-shipments/button-multiline-shipments-store';

type Props = {
    cartItem: ICartLineItem;
};

const ButtonImportCsv = observer(({cartItem}: Props) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (file) {
            await parse(file);
            e.target.value = '';
            buttonMultilineShipmentsStore.expandAll(cartItem);
        }
    };

    const parse = (file: File) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = event => {
                try {
                    const text = event.target?.result as string;

                    if (!text) {
                        alert('Could not read file');
                        reject('No file content');
                        return;
                    }

                    const lines = text.trim().split('\n');
                    const headers = lines[0]
                        .split(';')
                        .map(i => i.trim())
                        .filter(i => i);
                    const requiredFields = ['firstName', 'lastName', 'address1', 'city', 'state', 'postalCode', 'country'];
                    const missingHeaders = requiredFields.filter(field => !headers.includes(field));

                    if (missingHeaders.length > 0) {
                        alert(`Missing required fields in CSV: ${missingHeaders.join(', ')}`);
                        reject('Missing fields');
                        return;
                    }

                    const rows = lines.slice(1).map(line => {
                        const values = line.split(';').map(v => v.trim());
                        const rowObj: Record<string, string | boolean> = {};

                        headers.forEach((header, i) => {
                            rowObj[header] = values[i] || '';
                        });

                        return rowObj;
                    });

                    let result: Record<string, string | boolean>[];

                    if (rows.length === cartItem.qty) {
                        result = rows;
                    } else if (rows.length < cartItem.qty) {
                        const blanksToAdd = cartItem.qty - rows.length;

                        result = [...rows, ...Array.from({length: blanksToAdd}, () => Object.fromEntries(headers.map(header => [header, ''])))];
                        alert(`CSV contained ${rows.length} address${rows.length > 1 ? 'es' : ''}, but your cart has ${cartItem.qty} items. The rest will be left empty.`);
                    } else {
                        result = rows.slice(0, cartItem.qty);
                        alert(`CSV contained ${rows.length} addresses, but your cart only has ${cartItem.qty} items. Extra rows were ignored.`);
                    }

                    cartItem.address = cartItem.address.map((existing, index) => {
                        const rowData = result[index];
                        const updated = {...existing};

                        for (const key in rowData) {
                            const value = rowData[key];

                            if (key === 'isBusinessAddress') {
                                updated[key] = value === 'true';
                            } else if (updated[key]) {
                                updated[key] = {
                                    ...updated[key],
                                    value,
                                };
                            }
                        }

                        return updated;
                    });

                    resolve(undefined);
                } catch (e) {
                    reject(e);
                }
            };

            reader.readAsText(file);
        });
    };

    return (
        <>
            <button
                className="btn-checkout btn-checkout-white btn-shipping-white"
                type="button"
                aria-label="Import shipping addresses"
                onClick={handleClick}
            >
                Import
            </button>
            <span
                id="input-desc"
                className="sr-only"
            >
                Select a CSV file containing shipping addresses
            </span>
            <input
                type="file"
                accept=".csv"
                aria-describedby="input-desc"
                ref={fileInputRef}
                style={{display: 'none'}}
                onChange={handleFileSelect}
            />
        </>
    );
});

export default ButtonImportCsv;
